/**
 * Pure decision rule for what to tell the RN when the camera is unavailable.
 *
 * Deliberately import-free — no react-native, no expo-image-picker — so it can
 * be unit-tested directly. The Alert that renders the result lives in
 * `hooks/useUploadFlow.ts`.
 *
 * The distinction that matters is `canAskAgain`. A first refusal is recoverable
 * by tapping the button again, so nagging about Settings is noise. Once iOS or
 * Android stops offering the prompt, tapping again does nothing forever, and
 * Settings is the only way back — that is the case that has to be spelled out.
 * Mirrors the microphone handling in `useQuestionRecorder`.
 */

export interface CameraPermissionState {
    granted: boolean;
    /** expo-image-picker omits this on some platforms; treat missing as "yes". */
    canAskAgain?: boolean;
}

export type CameraPermissionOutcome =
    { kind: "granted" } | { kind: "denied"; message: string; offerSettings: boolean };

export function describeCameraPermission(perm: CameraPermissionState): CameraPermissionOutcome {
    if (perm.granted) {
        return { kind: "granted" };
    }
    // `canAskAgain !== false` on purpose, not `=== true`: an undefined value
    // means the platform did not say, and the recoverable message is the safe
    // guess — it never sends the RN to Settings for a prompt she can retry.
    if (perm.canAskAgain !== false) {
        return {
            kind: "denied",
            message: "Camera access is required to take a photo of the medication.",
            offerSettings: false
        };
    }
    return {
        kind: "denied",
        message:
            "Camera access was denied. Open Settings to enable it for this app, " +
            "or choose photos from your library instead.",
        offerSettings: true
    };
}

/**
 * What to say when the camera surface itself fails to open.
 *
 * Distinct from the permission check above: on the iOS Simulator permission IS
 * granted (there is no hardware to gate), so the permission path passes and
 * `launchCameraAsync` then rejects with `CameraUnavailableOnSimulatorException`
 * — see expo-image-picker's `ImagePickerModule.swift`, which returns that under
 * `#if targetEnvironment(simulator)`. Swallowing it into a console.error makes
 * "Take a photo" look like a dead button on every simulator build, which is
 * where this flow gets demoed and reviewed.
 */
export function describeCameraFailure(err: unknown): string {
    const text = err instanceof Error ? err.message : String(err ?? "");
    if (/simulator/i.test(text)) {
        return (
            'This simulator has no camera. Use "Choose from photos" instead, ' +
            "or run the app on a real device."
        );
    }
    return 'The camera could not be opened. Use "Choose from photos" instead.';
}
